import * as React from 'react';

import classnames from 'classnames';
import generateUniqueId from 'lodash/uniqueId';

import styles from './checkbox.module.css';

export type Props = {
    // TODO: Figure out how to make this work.  Might need a newer flow than 0.53
    // ...HTMLInputElement,
    label?: React.ReactNode;
    checked?: boolean;
    /** Should a larger box be shown (true), or the browser-default style (false)? */
    isLarge?: boolean;
    /** Should the checkbox be top aligned (aligned with the first line of text) */
    isTopAligned?: boolean;
    /** This should only be used to do things like stopPropagation */
    onClick?: (arg1: React.ChangeEvent<any>) => void;
    onChange?: (arg1: boolean, arg2: React.ChangeEvent<any>) => any;
    /** Sometimes we need to get the underlying node ref. */
    checkboxRef?: (arg1?: HTMLElement | null | undefined) => void;
    /** When there's an error (for example in validation), set this to true to get a red border */
    hasError?: boolean;
    helpComponent?: React.ReactNode;
    customCheckboxStyle?: any;
    disabled?: boolean | null | undefined;
    /** If true, the checkbox will show an X regardless of props.checked and props.disabled */
    showX?: boolean;
    labelColor?: 'light' | 'dark';
};

/**
 * The `isLarge` variant of checkboxes should be used for new UI development.
 *
 * Note: all valid props of an `input` element are also valid for this component.
 */
export const Checkbox = (props: Props) => {
    const {
        label = '',
        checkboxRef = () => {},
        onChange,
        checked = false,
        customCheckboxStyle,
        helpComponent,
        isLarge,
        hasError,
        isTopAligned,
        showX,
        labelColor = 'dark',
        ...restProps
    } = props;
    const [uniqueId] = React.useState(generateUniqueId('checkbox_'));

    const handleChange = React.useCallback(
        (e: React.ChangeEvent<any>) => {
            if (typeof onChange === 'function') {
                onChange(e.target.checked, e);
            }
        },
        [onChange]
    );

    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent<any>) => {
            if (e.key === 'Enter') {
                const newChecked = !checked;
                if (typeof onChange === 'function') {
                    const inputEvent = {
                        ...e,
                        target: {
                            ...e.target,
                            checked: newChecked,
                        },
                        currentTarget: e.currentTarget,
                        type: 'change',
                        nativeEvent: e.nativeEvent,
                    } as const;
                    onChange(newChecked, inputEvent);
                }
            }
        },
        [checked, onChange]
    );

    const containerStyle = classnames({
        [styles['is-large']]: isLarge,
        [styles.container]: !isLarge,
        [styles['has-error']]: hasError,
        [styles['is-top-aligned']]: isTopAligned,
        [styles['no-label']]: !label,
        [styles['is-x-state']]: showX,
        [styles['light-label']]: labelColor === 'light',
    });

    return (
        <div className={containerStyle}>
            <input
                {...restProps}
                type='checkbox'
                id={uniqueId}
                onChange={handleChange}
                style={customCheckboxStyle}
                onKeyDown={handleKeyDown}
                checked={checked}
                // @ts-expect-error
                disabled={props.disabled}
                ref={checkboxRef}
                className={styles.checkbox}
            />
            <label htmlFor={uniqueId} onClick={props.onClick}>
                {label}
            </label>
            {helpComponent || undefined}
        </div>
    );
};

Checkbox.displayName = 'Checkbox';
